import { Link, useParams, Navigate } from "react-router-dom";
import { PageShell } from "@/components/site/PageShell";
import { getArticleBySlug } from "@/content/articles";
import { useSeo } from "@/lib/seo";

const Article = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const origin = "https://www.adminlaget.se";

  useSeo({
    title: article ? `${article.title} | Adminlaget` : "Artikel hittades inte | Adminlaget",
    description: article?.description ?? "Artikeln kunde inte hittas.",
    canonical: article ? `${origin}/artiklar/${article.slug}` : undefined,
    ogType: "article",
    jsonLd: article
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.description,
          datePublished: article.publishedAt,
          dateModified: article.updatedAt ?? article.publishedAt,
          author: { "@type": "Organization", name: "Adminlaget" },
          publisher: {
            "@type": "Organization",
            name: "Adminlaget",
            url: origin,
          },
          mainEntityOfPage: `${origin}/artiklar/${article.slug}`,
          keywords: article.keywords.join(", "),
        }
      : undefined,
  });

  if (!article) {
    return <Navigate to="/artiklar" replace />;
  }

  return (
    <PageShell>
      <article className="max-w-[760px] mx-auto px-6 lg:px-8 py-16 md:py-20">
        <nav className="mb-8 text-sm">
          <Link to="/artiklar" className="text-ink-muted hover:text-oak transition-colors">
            ← Alla artiklar
          </Link>
        </nav>
        <header className="mb-10 pb-10 border-b border-border">
          <div className="flex items-center gap-3 text-xs text-ink-muted mb-4 uppercase tracking-wider">
            <time dateTime={article.publishedAt}>
              {new Date(article.publishedAt).toLocaleDateString("sv-SE", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </time>
            <span aria-hidden>·</span>
            <span>{article.readingMinutes} min läsning</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-ink text-balance">
            {article.title}
          </h1>
        </header>
        <div className="prose-article">{article.content}</div>
      </article>
    </PageShell>
  );
};

export default Article;
